/**
 * Cheq Conversion Tracking Module (rampjs)
 *
 * Self-registering module for Cheq integration on publisher-injected pages.
 *
 * Behaviour is a faithful port of the previous `cheq.ts` `initCheqRampJs`
 * handler: Cheq is treated as consent-exempt (it runs regardless of consent
 * state, matching today's ramp.js, which did not gate at all) and the rampjs
 * hard-coded `partnerPixels` are used as the fallback when the response does
 * not carry `cheq_settings.partner_pixels`.
 *
 * NOTE: the consent model for publisher-injected units vs. owned SERP pages is
 * being deliberately deferred to a pre-Phase-C spike (see
 * UNIFIED_RENDERER_CLIENT_PLAN.md §6). Until then this module preserves the
 * existing ungated behaviour via `consentExempt`.
 */

import { initCheq } from "@ascent/conversion-tracking";
import type {
  CheqSettings,
  PartnerPixelConfig,
  Response,
} from "@ascent/papi-core";
import { registerTrackingPartner } from "../../tracking-registry";
import type { TrackingModule } from "../tracking-module";

/**
 * RampJS partner pixels fallback.
 *
 * In the previous rampjs code these were hard-coded. allow_negative_audience
 * (when true) fires the `cheq_neg_audience_event` plus these partner events:
 *   - taboola (id: "1070267", event: "CHEQ4PPC")
 *   - zemanta (id: "64194", event: "Content_View")
 *
 * Ideally this should come from papi-core, but for now it is kept here to
 * preserve existing behaviour and is only used when the response omits
 * `cheq_settings.partner_pixels`.
 */
const partnerPixels: PartnerPixelConfig[] = [
  {
    partner_name: "taboola",
    params: {
      partner: "taboola",
      id: "1070267",
      event: "CHEQ4PPC",
    },
    utm_source_prefixes: ["taboola"],
    enabled: true,
  },
  {
    partner_name: "zemanta",
    params: {
      partner: "zemanta",
      id: "64194",
      event: "Content_View",
    },
    utm_source_prefixes: ["zemanta"],
    enabled: true,
  },
];

export const CheqModule: TrackingModule = {
  id: "cheq",
  name: "Cheq Bot Detection",

  /**
   * Consent-exempt: Cheq claims their bot detection service operates under
   * GDPR "legitimate interest" for security/fraud prevention. This preserves
   * the previous ramp.js behaviour of running regardless of consent state.
   *
   * @see https://cheq.ai/data-privacy/
   */
  consentExempt: true,

  /** Ignored when consentExempt is true, but required by the interface. */
  requiresExplicitConsent: false,

  /**
   * Enabled by default; only disabled when cheq_settings.disable_cheq is true.
   * Null-safe because the rampjs server response may have no search.response.
   */
  isEnabled: (response: Response): boolean => {
    return !response?.page_options?.cheq_settings?.disable_cheq;
  },

  initialize: (response: Response): void => {
    const settings: CheqSettings = response?.page_options?.cheq_settings ?? {};

    initCheq(response?.request_id ?? "", response?.campaign_id ?? "", {
      allow_negative_audience: settings.allow_negative_audience,
      disable_cheq: settings.disable_cheq,
      class_name: settings.class_name,
      ch: settings.ch,
      cheq_neg_audience_event: settings.cheq_neg_audience_event,
      partner_pixels: settings.partner_pixels ?? partnerPixels,
    });
  },

  // No initializeLimited - conversion tracking requires full consent.
};

// Self-register on import.
registerTrackingPartner(CheqModule);

export { CheqModule as default };
